import { Box, Theme, SxProps } from '@mui/material';
import { IconButton, __SWEEP_DESIGN_TYPOGRAPHY_STYLES___ } from '@sweep-io/sweep-design';
import { Close } from '@sweep-io/sweep-design/dist/icons';
import { ReactNode, useRef, forwardRef, useImperativeHandle } from 'react';
import { ExpandedModeButton } from '../configuration-canvas/panels-reserve-space/ExpandedModeButton';
import { useExpandedMode } from '../configuration-canvas/panels-reserve-space/useExpandedMode';
import { useGetSidePadding } from './useGetSidePadding';
import { HORIZONTAL_PADDING } from './consts';

interface ConfigurationCanvasPanelHeaderProps {
  children?: ReactNode;
  onClose?: React.MouseEventHandler<HTMLButtonElement>; // If this is passed, a close button will be shown
  withExpandedModeButton?: boolean;
  customButton?: ReactNode; // Add the missing customButton prop

  keepHorizontalPadding?: boolean;
  sxProps?: SxProps<Theme>;
  closeButtonDataTestId?: string;
}

export interface ConfigurationCanvasPanelHeaderRef {
  getButtonsWidth: () => number;
}

export const ConfigurationCanvasPanelHeader = forwardRef<
  ConfigurationCanvasPanelHeaderRef,
  ConfigurationCanvasPanelHeaderProps
>(
  (
    {
      children,
      onClose,
      withExpandedModeButton,
      customButton,
      keepHorizontalPadding = false,
      sxProps,
      closeButtonDataTestId,
    },
    ref,
  ) => {
    const { isExpandedMode } = useExpandedMode();
    const { resetPanelWidthOnClose } = useExpandedMode();
    const sidePadding = useGetSidePadding();

    const containerRef = useRef<HTMLDivElement>(null);
    const buttonsRef = useRef<HTMLDivElement>(null);

    // Expose button width measurement to parent
    useImperativeHandle(
      ref,
      () => ({
        getButtonsWidth: () => {
          const buttonsWidth = buttonsRef.current?.offsetWidth ?? 0;
          const padding = (keepHorizontalPadding ? HORIZONTAL_PADDING : sidePadding) * 2;
          const gap = 8; // gap: 1 = 8px in MUI spacing

          return buttonsWidth + padding + gap; // Total space needed by buttons + spacing
        },
      }),
      [keepHorizontalPadding, sidePadding],
    );

    return (
      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          gap: 1,
          ...__SWEEP_DESIGN_TYPOGRAPHY_STYLES___[isExpandedMode ? 'h2' : 'h4-medium'],
          alignContent: 'center',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: `0 ${keepHorizontalPadding ? HORIZONTAL_PADDING : sidePadding}px`,
          minHeight: '32px',
          ...(sxProps ?? {}),
        }}
        className="configuration-canvas-panel-header"
      >
        <Box
          sx={{
            flex: '1 1 0',
            minWidth: 0, // Allows flex item to shrink below content size
            overflow: 'hidden',
          }}
        >
          {children ?? <div />}
        </Box>

        {(customButton || onClose || withExpandedModeButton) && (
          <Box ref={buttonsRef} display="flex" gap={1} alignItems="center" flexShrink={0}>
            {customButton}

            {withExpandedModeButton && <ExpandedModeButton />}

            {onClose && (
              <Box>
                <IconButton
                  data-testid={closeButtonDataTestId}
                  onClick={(event) => {
                    onClose?.(event);
                    resetPanelWidthOnClose?.();
                  }}
                  variant="flat"
                  size="tiny"
                >
                  <Close />
                </IconButton>
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  },
);
